import axios from 'axios'

const PLACES_URL = 'https://places.googleapis.com/v1'

export interface GooglePlaceSuggestion {
    placeId: string
    label: string
}

export interface GooglePlaceAddress {
    address1: string
    address2: string
    city: string
    postcode: string
    country: string
    // False when the user picked a street rather than a specific building
    hasBuildingNumber: boolean
}

interface AddressComponent {
    longText: string
    shortText: string
    types: string[]
}

const headers = (fieldMask?: string) => ({
    'Content-Type': 'application/json',
    'X-Goog-Api-Key': process.env.REACT_APP_GOOGLE_PLACES_API_KEY || '',
    ...(fieldMask ? { 'X-Goog-FieldMask': fieldMask } : {}),
})

// Autocomplete requests are free when the same sessionToken is passed to the Place Details call that follows
export async function GooglePlacesAutocomplete(input: string, sessionToken: string): Promise<GooglePlaceSuggestion[]> {
    try {
        const response = await axios.post(
            `${PLACES_URL}/places:autocomplete`,
            {
                input,
                sessionToken,
                includedPrimaryTypes: ['street_address', 'premise', 'subpremise', 'route'],
            },
            { headers: headers(), timeout: 10000 }
        )
        return (response.data.suggestions || [])
            .filter((s: any) => s.placePrediction)
            .map((s: any) => ({ placeId: s.placePrediction.placeId, label: s.placePrediction.text.text }))
    } catch (error) {
        return []
    }
}

export async function GooglePlaceDetails(placeId: string, sessionToken: string): Promise<GooglePlaceAddress | null> {
    try {
        // Only Essentials fields, so the lookup is billed at the cheapest Place Details rate
        const response = await axios.get(`${PLACES_URL}/places/${placeId}`, {
            params: { sessionToken },
            headers: headers('addressComponents,formattedAddress'),
            timeout: 10000,
        })
        const components: AddressComponent[] = response.data.addressComponents || []
        const find = (type: string) => components.find((c) => c.types.includes(type))

        const subpremise = find('subpremise')?.longText || ''
        // First line of the formatted address keeps each country's own street/number order
        const streetLine = (response.data.formattedAddress || '').split(',')[0].trim()
        const address1 =
            streetLine || [find('street_number')?.longText, find('route')?.longText].filter(Boolean).join(' ')

        return {
            address1,
            address2: subpremise && !address1.includes(subpremise) ? subpremise : '',
            city:
                find('locality')?.longText ||
                find('postal_town')?.longText ||
                find('administrative_area_level_2')?.longText ||
                '',
            postcode: find('postal_code')?.longText || '',
            country: find('country')?.shortText || '',
            hasBuildingNumber: Boolean(find('street_number') || find('premise') || subpremise || /\d/.test(address1)),
        }
    } catch (error) {
        return null
    }
}
